<div class="dr-wrap">

  <!-- Header row -->
  <div class="dr-header-row">
    <h3 class="dr-section-title">Daily Field Reports</h3>
    <button mat-raised-button color="primary" (click)="openAdd()" *ngIf="!showForm">
      <mat-icon>add</mat-icon> Add Report
    </button>
  </div>

  <!-- Add / Edit form -->
  <div class="dr-form-card" *ngIf="showForm" [formGroup]="form">
    <h4 class="dr-form-title">{{ editingReport ? 'Edit Report' : 'New Daily Report' }}</h4>

    <div class="dr-form-grid">
      <mat-form-field appearance="outline">
        <mat-label>Report Date</mat-label>
        <input matInput type="date" formControlName="reportDate">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Foreman / Superintendent</mat-label>
        <input matInput formControlName="foremanName" placeholder="Name">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Weather</mat-label>
        <mat-select formControlName="weatherConditions">
          <mat-option *ngFor="let w of weatherOptions" [value]="w">{{ w }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Temperature</mat-label>
        <input matInput formControlName="temperature" placeholder="e.g. 72°F">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Workers On Site</mat-label>
        <input matInput type="number" formControlName="workersOnSite" min="0">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Hours Worked</mat-label>
        <input matInput type="number" formControlName="hoursWorked" min="0" max="24">
      </mat-form-field>
    </div>

    <mat-form-field appearance="outline" class="dr-full">
      <mat-label>Work Completed Today</mat-label>
      <textarea matInput formControlName="workCompleted" rows="3" placeholder="Describe work completed..."></textarea>
    </mat-form-field>

    <mat-form-field appearance="outline" class="dr-full">
      <mat-label>Materials Used</mat-label>
      <textarea matInput formControlName="materialsUsed" rows="2" placeholder="List materials used..."></textarea>
    </mat-form-field>

    <mat-form-field appearance="outline" class="dr-full">
      <mat-label>Issues / Delays</mat-label>
      <textarea matInput formControlName="issuesEncountered" rows="2" placeholder="Any issues encountered..."></textarea>
    </mat-form-field>

    <mat-form-field appearance="outline" class="dr-full">
      <mat-label>Safety Incidents</mat-label>
      <textarea matInput formControlName="safetyIncidents" rows="2" placeholder="Safety incidents or near-misses (none if blank)..."></textarea>
    </mat-form-field>

    <div class="dr-inspection-row">
      <mat-checkbox formControlName="requiresInspection" color="primary">Inspection Required</mat-checkbox>
      <mat-form-field appearance="outline" class="dr-inspection-detail" *ngIf="form.get('requiresInspection')?.value">
        <mat-label>Inspection Details</mat-label>
        <input matInput formControlName="inspectionDetails" placeholder="What type of inspection...">
      </mat-form-field>
    </div>

    <mat-form-field appearance="outline" class="dr-full">
      <mat-label>Plan for Tomorrow</mat-label>
      <textarea matInput formControlName="nextDayPlan" rows="2" placeholder="What's planned for next day..."></textarea>
    </mat-form-field>

    <div class="dr-form-actions">
      <button mat-stroked-button (click)="cancel()">Cancel</button>
      <button mat-raised-button color="primary" (click)="submit()">
        <mat-icon>save</mat-icon> {{ editingReport ? 'Update' : 'Save Report' }}
      </button>
    </div>
  </div>

  <!-- Loading -->
  <div class="dr-loading" *ngIf="isLoading">
    <div class="css-spinner"></div>
  </div>

  <!-- Empty state -->
  <div class="dr-empty" *ngIf="!isLoading && reports.length === 0 && !showForm">
    <mat-icon>assignment</mat-icon>
    <p>No daily reports yet. Add the first report for today.</p>
  </div>

  <!-- Report cards -->
  <div class="dr-cards" *ngIf="!isLoading && reports.length > 0">
    <div class="dr-card" *ngFor="let report of reports"
         [class.dr-card-issues]="hasIssues(report)"
         [class.dr-card-inspection]="hasInspection(report)">

      <div class="dr-card-header">
        <div class="dr-card-date">
          <mat-icon>calendar_today</mat-icon>
          {{ report.reportDate | date:'EEEE, MMM d, y' }}
        </div>
        <div class="dr-card-badges">
          <span class="dr-badge dr-badge-inspection" *ngIf="hasInspection(report)">
            <mat-icon>fact_check</mat-icon> Inspection
          </span>
          <span class="dr-badge dr-badge-issue" *ngIf="hasIssues(report)">
            <mat-icon>warning</mat-icon> Issues
          </span>
        </div>
        <div class="dr-card-actions">
          <button mat-icon-button (click)="openEdit(report)" matTooltip="Edit">
            <mat-icon>edit</mat-icon>
          </button>
          <button mat-icon-button color="warn" (click)="remove(report)" matTooltip="Delete">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      </div>

      <div class="dr-card-meta">
        <span><mat-icon>person</mat-icon> {{ report.foremanName }}</span>
        <span><mat-icon>wb_sunny</mat-icon> {{ report.weatherConditions }} {{ report.temperature }}</span>
        <span><mat-icon>groups</mat-icon> {{ report.workersOnSite }} workers</span>
        <span><mat-icon>schedule</mat-icon> {{ report.hoursWorked }} hrs</span>
      </div>

      <div class="dr-card-section">
        <strong>Work Completed</strong>
        <p>{{ report.workCompleted }}</p>
      </div>

      <div class="dr-card-section" *ngIf="report.materialsUsed">
        <strong>Materials Used</strong>
        <p>{{ report.materialsUsed }}</p>
      </div>

      <div class="dr-card-section dr-section-issue" *ngIf="report.issuesEncountered">
        <strong><mat-icon>warning_amber</mat-icon> Issues / Delays</strong>
        <p>{{ report.issuesEncountered }}</p>
      </div>

      <div class="dr-card-section dr-section-safety" *ngIf="report.safetyIncidents">
        <strong><mat-icon>health_and_safety</mat-icon> Safety</strong>
        <p>{{ report.safetyIncidents }}</p>
      </div>

      <div class="dr-card-section dr-section-inspection" *ngIf="report.requiresInspection">
        <strong><mat-icon>fact_check</mat-icon> Inspection Required</strong>
        <p>{{ report.inspectionDetails }}</p>
      </div>

      <div class="dr-card-section dr-section-tomorrow" *ngIf="report.nextDayPlan">
        <strong>Tomorrow's Plan</strong>
        <p>{{ report.nextDayPlan }}</p>
      </div>

    </div>
  </div>

</div>
